import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  VStack,
  Text,
  Box,
  useToast,
} from '@chakra-ui/react';
import { trackEvent } from '../utils/analytics';

const JOIN_ENDPOINT = 'https://yetzira.ai/join';

const initialForm = {
  name: '',
  email: '',
  organization: '',
  businessType: '',
  description: '',
};

export default function JoinModal({ isOpen, onClose }) {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!form.name.trim()) return 'Please enter your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Please enter a valid email.';
    if (!form.description.trim()) return 'Please tell us what you want to build.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      toast({
        title: error,
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'bottom',
      });
      return;
    }

    setLoading(true);
    trackEvent('join_submit', { businessType: form.businessType });

    try {
      const res = await fetch(JOIN_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, submittedAt: new Date().toISOString() }),
      });

      if (!res.ok) throw new Error(`Request failed: ${res.status}`);

      toast({
        title: 'Welcome aboard.',
        description: 'An agent will reach out shortly with your next steps.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
      setForm(initialForm);
      onClose();
    } catch (err) {
      toast({
        title: 'Something went wrong.',
        description: 'Please try again, or email contact@agents.yetzira.ai',
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setLoading(false);
    }
  };

  const fieldStyles = {
    bg: 'bg.700',
    border: '1px solid',
    borderColor: 'whiteAlpha.200',
    color: 'text.primary',
    borderRadius: '10px',
    _placeholder: { color: 'text.muted' },
    _focus: {
      borderColor: 'brand.500',
      boxShadow: '0 0 0 1px rgba(99,102,241,0.4)',
    },
  };

  const labelStyles = {
    fontSize: '0.85rem',
    fontWeight: '500',
    color: 'text.primary',
    mb: 1,
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay bg="rgba(10,11,15,0.8)" backdropFilter="blur(8px)" />
      <ModalContent
        bg="bg.800"
        border="1px solid"
        borderColor="whiteAlpha.100"
        borderRadius="20px"
        mx={4}
      >
        <ModalHeader pb={1}>
          <Text
            fontFamily="'JetBrains Mono', monospace"
            fontSize="0.72rem"
            textTransform="uppercase"
            letterSpacing="1.5px"
            color="brand.300"
            mb={2}
          >
            Join Yetzira
          </Text>
          <Text
            fontFamily="'Space Grotesk', sans-serif"
            fontWeight="700"
            fontSize="1.6rem"
            color="text.primary"
            letterSpacing="-0.02em"
          >
            Build your organization under Yetzira.
          </Text>
          <Text fontSize="0.95rem" color="text.muted" fontWeight="400" mt={2} lineHeight="1.6">
            Tell us what you want to create. An agent will reach out with the next steps to spin
            up a new autonomous organization on the Yetzira architecture.
          </Text>
        </ModalHeader>
        <ModalCloseButton color="text.muted" mt={2} />

        <ModalBody>
          <form onSubmit={handleSubmit} id="join-form">
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel {...labelStyles}>Your name</FormLabel>
                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Jane Doe"
                  {...fieldStyles}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel {...labelStyles}>Email</FormLabel>
                <Input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  {...fieldStyles}
                />
              </FormControl>

              <FormControl>
                <FormLabel {...labelStyles}>Organization name (optional)</FormLabel>
                <Input
                  name="organization"
                  value={form.organization}
                  onChange={handleChange}
                  placeholder="What it's called, if it exists yet"
                  {...fieldStyles}
                />
              </FormControl>

              <FormControl>
                <FormLabel {...labelStyles}>Type of business</FormLabel>
                <Select
                  name="businessType"
                  value={form.businessType}
                  onChange={handleChange}
                  placeholder="Select a category"
                  sx={{
                    '> option': { background: '#161922', color: '#F4F6FB' },
                  }}
                  {...fieldStyles}
                >
                  <option value="saas">Software / SaaS</option>
                  <option value="services">Professional Services</option>
                  <option value="ecommerce">E-commerce / Retail</option>
                  <option value="content">Content / Media</option>
                  <option value="finance">Finance / Fintech</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="other">Other</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel {...labelStyles}>What do you want to build?</FormLabel>
                <Textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Describe the organization you want to create — who it serves, what it should do, and the outcome you're after."
                  rows={5}
                  resize="vertical"
                  {...fieldStyles}
                />
              </FormControl>
            </VStack>
          </form>
        </ModalBody>

        <ModalFooter gap={3}>
          <Button variant="outline_brand" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="gold" type="submit" form="join-form" isLoading={loading} px={8}>
            Join Yetzira
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
